import { useLanguageStore } from '@/stores/LanguageStore'
import { z } from 'zod'

export type LoginForm = {
  email: string
  password: string
}

export type LoginErrors = Partial<Record<keyof LoginForm, string>>

export const useLoginValidator = () => {
  const languageStore = useLanguageStore()

  const normalizeCredential = (credential: string) => credential.trim()

  const buildLocalLoginSchema = () =>
    z.object({
      email: z
        .string()
        .trim()
        .min(1, languageStore.t('auth.login.validation.emailRequired'))
        .email(languageStore.t('auth.login.validation.invalidEmail')),
      password: z
        .string()
        .min(1, languageStore.t('auth.login.validation.passwordRequired')),
    })

  const buildAdLoginSchema = () =>
    z.object({
      email: z
        .string()
        .trim()
        .min(1, languageStore.t('auth.login.validation.adCredentialRequired')),
      password: z
        .string()
        .min(1, languageStore.t('auth.login.validation.passwordRequired')),
    })

  const createForm = (): LoginForm => ({
    email: '',
    password: '',
  })

  const createEmptyErrors = (): LoginErrors => ({
    email: '',
    password: '',
  })

  const validateSchema = (
    schema: z.ZodSchema<LoginForm>,
    form: LoginForm,
  ): { isValid: boolean; errors: LoginErrors } => {
    const errors = createEmptyErrors()

    try {
      schema.parse(form)
      return { isValid: true, errors }
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((e) => {
          const field = e.path[0] as keyof LoginForm
          if (!errors[field]) {
            errors[field] = e.message
          }
        })
      }

      return { isValid: false, errors }
    }
  }

  const validateLocalForm = (form: LoginForm) => validateSchema(buildLocalLoginSchema(), form)

  const validateAdForm = (form: LoginForm) => validateSchema(buildAdLoginSchema(), form)

  const resetForm = (form: LoginForm): void => {
    form.password = ''
    form.email = ''
  }

  const resetErrors = (errors: LoginErrors, field?: keyof LoginErrors): void => {
    if (field) {
      errors[field] = ''
    } else {
      Object.keys(errors).forEach((key) => {
        errors[key as keyof LoginErrors] = ''
      })
    }
  }

  return {
    createForm,
    createEmptyErrors,
    normalizeCredential,
    validateLocalForm,
    validateAdForm,
    resetForm,
    resetErrors,
  }
}
